import { SimplePhysicsEngine, GameEngine, TwoVector, ThreeVector, Quaternion, Serializer, Serializable } from 'lance-gg';
import Ship from './Ship';
import Missile from './Missile';

// Runtime fix for lance-gg Serializer classId collisions under JS minification
if (Serializer && !Serializer._minificationPatched) {
    Serializer._minificationPatched = true;

    const hashStr = (str, bits = 8) => {
        let hash = 5381;
        let i = str.length;
        while (i) {
            hash = (hash * 33) ^ str.charCodeAt(--i);
        }
        hash = hash >>> 0;
        return hash % (Math.pow(2, bits) - 1);
    };

    if (TwoVector) TwoVector.classId = hashStr('TwoVector');
    if (ThreeVector) ThreeVector.classId = hashStr('ThreeVector');
    if (Quaternion) Quaternion.classId = hashStr('Quaternion');

    Serializer.prototype.registerClass = function(classObj, classId) {
        if (typeof classId === 'string') {
            classId = hashStr(classId);
        }
        if (classId === undefined || classId === null) {
            if (classObj && classObj.classId !== undefined) {
                classId = classObj.classId;
            } else if (classObj && classObj.netScheme && classObj.netScheme.classId !== undefined) {
                classId = classObj.netScheme.classId;
            } else if (classObj && classObj.name && classObj.name.length > 2) {
                classId = hashStr(classObj.name);
            } else {
                this._autoClassId = (this._autoClassId || 200) + 1;
                classId = this._autoClassId;
            }
        }

        if (this.registeredClasses[classId]) {
            console.warn(`Serializer: registering classId ${classId}`, classObj);
        }

        this.registeredClasses[classId] = classObj;
        if (classObj) {
            classObj.classId = classId;
            if (classObj.prototype) {
                classObj.prototype.classId = classId;
            }
        }
    };

    if (Serializable && Serializable.prototype) {
        const origSerialize = Serializable.prototype.serialize;
        Serializable.prototype.serialize = function(serializer, options) {
            if (!this.classId && this.constructor && this.constructor.classId) {
                this.classId = this.constructor.classId;
            }
            return origSerialize.call(this, serializer, options);
        };
    }
}

export default class SpaaaceGameEngine extends GameEngine {

    constructor(options) {
        super(options);
        this.physicsEngine = new SimplePhysicsEngine({
            gameEngine: this,
            collisions: {
                type: 'brute',
                collisionDistance: 28
            }
        });
    }

    registerClasses(serializer){
        serializer.registerClass(Ship, 'Ship');
        serializer.registerClass(Missile, 'Missile');
    }

    initWorld(){
        super.initWorld({
            worldWrap: true,
            width: 3000,
            height: 3000
        });
    }

    start() {
        super.start();

        this.on('collisionStart', e => {
            let collisionObjects = Object.keys(e).map(k => e[k]);
            let ship = collisionObjects.find(o => o instanceof Ship);
            let missile = collisionObjects.find(o => o instanceof Missile);

            if (!ship || !missile)
                return;

            // make sure not to process the collision between a missile and the ship that fired it
            if (missile.playerId !== ship.playerId) {
                this.destroyMissile(missile.id);
                this.trace.info(() => `missile by ship=${missile.playerId} hit ship=${ship.id}`);
                this.emit('missileHit', { missile, ship });
            }
        });

        this.on('postStep', this.reduceVisibleThrust.bind(this));
    };

    processInput(inputData, playerId, isServer) {

        super.processInput(inputData, playerId);

        // get the player ship tied to the player socket
        let playerShip = this.world.queryObject({
            playerId: playerId,
            instanceType: Ship
        });

        if (playerShip) {
            if (inputData.input == 'up') {
                playerShip.accelerate(0.05);
                playerShip.showThrust = 5; // show thrust for next steps.
            } else if (inputData.input == 'right') {
                playerShip.turnRight(2.5);
            } else if (inputData.input == 'left') {
                playerShip.turnLeft(2.5);
            } else if (inputData.input == 'space') {
                this.makeMissile(playerShip, inputData.messageIndex);
                this.emit('fireMissile');
            }
        }
    };

    // Makes a new ship, places it randomly and adds it to the game world
    makeShip(playerId) {
        let newShipX = Math.floor(Math.random()*(this.worldSettings.width-200)) + 200;
        let newShipY = Math.floor(Math.random()*(this.worldSettings.height-200)) + 200;

        let ship = new Ship(this, null, {
            position: new TwoVector(newShipX, newShipY)
        });

        ship.playerId = playerId;
        this.addObjectToWorld(ship);
        console.log(`ship added: ${ship.toString()}`);

        return ship;
    };

    makeMissile(playerShip, inputId) {
        let missile = new Missile(this);

        // we want the missile location and velocity to correspond to that of the ship firing it
        missile.position.copy(playerShip.position);
        missile.velocity.copy(playerShip.velocity);
        missile.angle = playerShip.angle;
        missile.playerId = playerShip.playerId;
        missile.ownerId = playerShip.id;
        missile.inputId = inputId; // this enables usage of the missile shadow object
        missile.velocity.x += Math.cos(missile.angle * (Math.PI / 180)) * 10;
        missile.velocity.y += Math.sin(missile.angle * (Math.PI / 180)) * 10;

        this.trace.trace(() => `missile[${missile.id}] created vel=${missile.velocity}`);

        let obj = this.addObjectToWorld(missile);

        // if the object was added successfully to the game world, destroy the missile after some game ticks
        if (obj)
            this.timer.add(30, this.destroyMissile, this, [obj.id]);

        return missile;
    }

    // destroy the missile if it still exists
    destroyMissile(missileId) {
        if (this.world.objects[missileId]) {
            this.trace.trace(() => `missile[${missileId}] destroyed`);
            this.removeObjectFromWorld(missileId);
        }
    }

    // at the end of the step, reduce the thrust for all objects
    reduceVisibleThrust(postStepEv) {
        if (postStepEv.isReenact)
            return;

        let ships = this.world.queryObjects({
            instanceType: Ship
        });

        ships.forEach(ship => {
            if (Number.isInteger(ship.showThrust) && ship.showThrust >= 1)
                ship.showThrust--;
        });
    }
}
